
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { safeMaybeSingle, safeInsert } from "@/utils/supabase/queries";
import { isDatabaseRecord, toDatabaseValue } from "@/utils/supabase/type-guards";

export const useGroupActions = (groupId: string, userId?: string) => {
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const joinGroup = async () => {
    if (!userId) {
      toast.error("You must be logged in to join a group");
      return false;
    }

    setIsJoining(true);
    try {
      // Check if user is already a member
      const existingMember = await safeMaybeSingle<any>(
        supabase
          .from("group_members")
          .select("*")
          .eq("group_id", toDatabaseValue(groupId))
          .eq("user_id", toDatabaseValue(userId)),
        "Failed to check membership"
      );

      if (existingMember && isDatabaseRecord(existingMember)) {
        if (existingMember.is_banned) {
          toast.error("You are banned from this group");
          return false;
        }
        toast.info("You are already a member of this group");
        return true;
      }

      // Join the group
      const { data: memberData, error } = await supabase
        .from("group_members")
        .insert(toDatabaseValue({
          group_id: groupId,
          user_id: userId,
          role: "member",
          is_banned: false,
          is_muted: false
        }))
        .select()
        .single();

      if (error) throw error;

      if (memberData && isDatabaseRecord(memberData)) {
        toast.success("Successfully joined the group!");
        return true;
      }
      
      return false;
    } catch (error: any) {
      console.error("Error joining group:", error);
      toast.error(error.message || "Failed to join group");
      return false;
    } finally {
      setIsJoining(false);
    }
  };

  const leaveGroup = async () => {
    if (!userId) {
      toast.error("You must be logged in to leave a group");
      return false;
    }

    setIsLeaving(true);
    try {
      // Check if user is a member
      const memberData = await safeMaybeSingle<any>(
        supabase
          .from("group_members")
          .select("*")
          .eq("group_id", toDatabaseValue(groupId))
          .eq("user_id", toDatabaseValue(userId)),
        "Failed to check membership"
      );

      if (!memberData || !isDatabaseRecord(memberData)) {
        toast.error("You are not a member of this group");
        return false;
      }

      // Remove membership
      const { error } = await supabase
        .from("group_members")
        .delete()
        .eq("id", toDatabaseValue(memberData.id));

      if (error) throw error;

      toast.success("Successfully left the group");
      return true;
    } catch (error: any) {
      console.error("Error leaving group:", error);
      toast.error(error.message || "Failed to leave group");
      return false;
    } finally {
      setIsLeaving(false);
    }
  };

  return {
    joinGroup,
    leaveGroup,
    isJoining,
    isLeaving
  };
};
